/**
 * Desk Setup habit engine — pure logic and wording.
 * Reps, targets and switches are data (desk_habit_reps / desk_habit_settings, admin-editable);
 * counting lives in one server function (get_desk_habit_progress). This module only decides
 * WHICH rep to suggest where, and HOW to say it. Swap either without touching the screens.
 */
import { setupLine, setupButton, setupLabel } from '../../supabase/functions/_shared/deskNextMoveCopy';

export interface HabitRep {
  key: string;
  label: string;
  count: number;
  target: number;
  done: boolean;
  capability: string | null;
  route: string;
  cue: string | null;
}

export interface HabitProgress {
  /** Server-resolved persona (desk_company_persona). */
  persona?: HabitPersona;
  reps: HabitRep[];
  graduated: boolean;
  graduatedAt: string | null;
  version: number;
  ribbonEnabled: boolean;
  celebrationsEnabled: boolean;
}

export type HabitPersona = 'seller' | 'buyer' | 'power_trader';

const basePath = (to: string) => to.split(/[?#]/)[0];

/** Reps the person's current plan can actually do. */
export function visibleReps(p: HabitProgress, can: (capability: string) => boolean): HabitRep[] {
  return p.reps.filter((r) => !r.capability || can(r.capability));
}

/** Overall progress, 0–1, capped per rep so one over-achieved rep can't hide the others. */
export function habitFraction(reps: HabitRep[]): number {
  if (!reps.length) return 0;
  return reps.reduce((s, r) => s + Math.min(1, r.count / Math.max(1, r.target)), 0) / reps.length;
}

/** One suggestion: an unmet rep that belongs to this page wins; otherwise the next unmet rep in order. */
export function pickHabitRep(pathname: string, reps: HabitRep[]): HabitRep | null {
  const open = reps.filter((r) => !r.done);
  return open.filter(isSuggestable).find((r) => basePath(r.route) === pathname) ?? open.filter(isSuggestable)[0] ?? null;
}

/** Ribbon line per step — copy table, Your Next Move™ spec Phase 2 (shared with the brief and Ask Cannafax). */
export function repLine(r: HabitRep, persona?: HabitPersona): string {
  return setupLine(r.key, r.count, r.target, r.label, persona);
}

/** Step name per persona (brokers/power traders get supply/demand wording). */
export function repLabel(r: HabitRep, persona?: HabitPersona): string {
  return setupLabel(r.key, r.label, persona);
}

/** Button label per step — always names the action. */
export function repButton(r: HabitRep, persona?: HabitPersona): string | null {
  return setupButton(r.key, persona);
}

/** Every setup step is something the person can do themselves, so any unfinished step can be suggested. */
export function isSuggestable(r: HabitRep): boolean {
  return !r.done;
}

export const DESK_LIST_TITLE = 'Make yourself at home.';
export const deskListSubtitle = (persona: HabitPersona) =>
  persona === 'buyer' ? 'Here’s what helps you find product faster. Start anywhere.'
    : persona === 'power_trader' ? 'Here’s what gets your listings in front of buyers and finds product for them. Start anywhere.'
    : 'Here’s what gets your listings in front of buyers. Start anywhere.';
/** Big headline above the progress bar: how many steps stand between them and the payoff. */
export function deskSetupStepsLeft(done: number, total: number): string {
  const remaining = Math.max(0, total - done);
  return remaining === 0 ? 'Almost there' : `${remaining} ${remaining === 1 ? 'step' : 'steps'} left`;
}
/** The promise under the headline: what finishing unlocks, in the seller's (or buyer's) money terms. */
export const deskSetupPayoff = (persona: HabitPersona) =>
  persona === 'buyer' ? 'Then Your Next Move™ will watch your supplier menus and let you know as soon as new inventory is available.'
    : persona === 'power_trader' ? 'Then we tell you who to call to move inventory, and Your Next Move™ lets you know what your buyers are looking for.'
    : 'Then we tell you who to call to move your inventory.';
/** One-line form for compact surfaces. */
export function deskSetupProgressLine(done: number, total: number, persona: HabitPersona = 'seller'): string {
  return `${deskSetupStepsLeft(done, total)} — ${deskSetupPayoff(persona).replace(/^Then/, 'then')}`;
}
export const DESK_STEP_DONE = 'Done.';
export const HABIT_DONE_LINE = 'You’re all set up. From here, Your Next Move™ lets you know the one thing worth doing each day.';

export type CelebrationVariant = 'first' | 'middle' | 'target';
/** Post-publish moment for listings 1..target; null after that (ordinary toast only). */
export function lotCelebrationCopy(n: number, target: number): { title: string; body: string; variant: CelebrationVariant } | null {
  if (n < 1 || n > target) return null;
  if (n === 1) return { variant: 'first', title: 'Your first listing is live.', body: 'Add another, or put it on a menu so buyers can see it.' };
  if (n === target) return { variant: 'target', title: `${target} listings live. That’s a real menu.`, body: 'Put them on a menu and send it to the buyers you know.' };
  return { variant: 'middle', title: `Listing ${n} is live.`, body: 'Add another, or put what you have on a menu.' };
}

export const LOT_PUBLISHED_EVENT = 'desk:lot-published';

const STEP_NOUN: Record<string, [string, string]> = {
  logo: ['brand set', 'brand set'], lots: ['listing live', 'listings live'], menus: ['VIP menu set up', 'VIP menus set up'], shares: ['menu link shared', 'menu links shared'],
  invites: ['buyer invited', 'buyers invited'],
  supplier_menus: ['supplier menu connected', 'supplier menus connected'], hunting: ['search saved', 'searches saved'],
  alerts: ['alert on', 'alerts on'], buyer_messages: ['message sent', 'messages sent'],
};
/** Plain progress of one step, e.g. "1 of 7 listings live". */
export function stepProgress(r: HabitRep): string {
  const [one, many] = STEP_NOUN[r.key] ?? ['done', 'done'];
  return `${Math.min(r.count, r.target)} of ${r.target} ${r.target === 1 ? one : many}`;
}

export const INGEST_ADDRESS = 'ingest@cannafax.ai';
export const DESK_SETUP_HELP_LINE = `Short on time? Forward the inventory email or sheet you already send to ${INGEST_ADDRESS} and we’ll set it up.`;
